import posthog from "posthog-js";

/* ─── Types ──────────────────────────────────────── */

/**
 * Legacy enums (kept to avoid a multi-file migration right now).
 * We normalize these to the spec enums inside track().
 */
type ViewerType = "anon" | "auth" | "producer_member";
type Source = "explore" | "detail" | "router" | "share";
type Surface = "mobile" | "desktop";

/**
 * Spec enums (docs/analytics/analytics-events.md).
 * These are what we actually send to PostHog.
 */
type SpecViewerType = "anon" | "viewer" | "producer" | "admin" | "service";
type SpecSource = "direct" | "internal" | "share" | "search" | "social" | "email";
type SpecSurface =
  | "lens"
  | "place"
  | "producer"
  | "activity"
  | "explore_list"
  | "explore_map"
  | "producer_portal"
  | "admin_portal";

interface BaseProps {
  env: string;
  viewer_type: SpecViewerType;
  source: SpecSource;
  surface: SpecSurface;
}

/* ─── Normalization (compat layer) ───────────────── */

function normalizeViewerType(v: ViewerType): SpecViewerType {
  switch (v) {
    case "anon":
      return "anon";
    case "auth":
      // Legacy "auth" = logged-in viewer (non-producer)
      return "viewer";
    case "producer_member":
      return "producer";
    default:
      return "anon";
  }
}

/**
 * Legacy Source ("explore/detail/router/share") doesn't match spec ("direct/internal/share/...").
 * We map conservatively:
 * - router/explore/detail => internal
 * - share => share
 */
function normalizeSource(s: Source): SpecSource {
  switch (s) {
    case "share":
      return "share";
    case "explore":
    case "detail":
    case "router":
      return "internal";
    default:
      return "internal";
  }
}

/**
 * Surface in spec is about UI area, not device.
 * We only know device today, so we choose a safe default.
 *
 * IMPORTANT: to get perfect `surface`, we should later add a `setSurface(...)`
 * that sets spec surfaces from the router (place/activity/lens/etc).
 */
let _specSurface: SpecSurface = "activity"; // safe default for public flows
function normalizeSurface(_legacy: Surface): SpecSurface {
  // If we have explicitly set a spec surface, use it.
  return _specSurface;
}

/**
 * Set spec surface explicitly (e.g. from a page component).
 */
export function setSpecSurface(surface: SpecSurface) {
  _specSurface = surface;
}

/**
 * Derive spec surface from a pathname.
 * Returns undefined for routes that don't map cleanly.
 */
export function resolveSpecSurfaceFromPath(pathname: string): SpecSurface | undefined {
  const p = pathname.toLowerCase();
  if (p.startsWith("/admin")) return "admin_portal";
  if (p.startsWith("/producer") || p.startsWith("/vard")) return "producer_portal";
  if (p.startsWith("/utforska") && p.includes("karta")) return "explore_map";
  if (p.startsWith("/utforska")) return "explore_list";
  if (p.startsWith("/plats") || p.startsWith("/place")) return "place";
  if (p.startsWith("/aktivitet") || p.startsWith("/activity") || p.startsWith("/upplevelse")) return "activity";
  if (p.startsWith("/lens") || p === "/") return "lens";
  return undefined;
}

/**
 * Convenience: resolve surface from pathname and set it.
 * No-op if the pathname doesn't map to a known surface.
 */
export function setSpecSurfaceFromPath(pathname: string) {
  const surface = resolveSpecSurfaceFromPath(pathname);
  if (surface) _specSurface = surface;
}

/**
 * Returns true if PostHog has been initialised (i.e. key was present).
 * Useful for debug UIs and conditional logging.
 */
export function isAnalyticsEnabled(): boolean {
  return _initialized;
}

/**
 * Send a native PostHog `$pageview` with optional extra properties.
 *
 * PostHog Web Analytics relies on `$pageview` — this helper lets you
 * emit one explicitly (e.g. for virtual navigations not caught by
 * the automatic SPA listener). In most cases PostHog captures
 * pageviews automatically because `capture_pageview: true`.
 */
export function trackPageView(extra?: Record<string, unknown>) {
  if (!_initialized) {
    if (_env === "dev") console.debug("[analytics] trackPageView (disabled)", extra);
    return;
  }
  posthog.capture("$pageview", extra);
}

/* ─── Events ─────────────────────────────────────── */

export interface AnalyticsEvents {
  // NOTE: Pageviews are handled by PostHog native $pageview (capture_pageview: true).
  // Do NOT add a custom "page_view" event here — use trackPageView() for explicit virtual pageviews.
  outing_section_view: { section_name: string; items_count: number };
  outing_card_click: {
    outing_id: string;
    badge: string | null;
    date_bucket: string;
    distance_km_bucket: string;
  };
  outing_detail_view: { outing_id: string };
  outing_save_toggle: { outing_id: string; saved: boolean };
  filter_toggle: { filter_key: string; enabled: boolean };
  explore_path_click: { path: string; mode: string };
  location_change: { region: string };
  explore_near_me_click: {
    action: "enable" | "disable";
    had_location_cached: boolean;
    view_mode: "map" | "list";
    radius_bucket: string;
  };
  explore_near_me_result: {
    ok: boolean;
    error?: string;
    radius_bucket: string;
  };
  explore_results_view: {
    mode: "places" | "farms" | "do";
    count: number;
    count_bucket: string;
    mappable_count: number;
    mappable_bucket: string;
    near_me: boolean;
    near_me_bucket: "yes" | "no";
    sort: string;
    has_search: boolean;
    has_search_bucket: "yes" | "no";
    has_filters: boolean;
    has_filters_bucket: "yes" | "no";
    is_fallback: boolean;
    is_fallback_bucket: "yes" | "no";
    has_geo: boolean;
    has_geo_bucket: "yes" | "no";
    radius_km: number;
    radius_bucket: string;
  };
  explore_rail_select: {
    kind: "place" | "producer" | "activity";
    position_bucket: "1" | "2–3" | "4–10" | "11+";
    distance_km_bucket: string;
  };
  explore_rail_open: {
    kind: "place" | "producer" | "activity";
    action: "preview" | "navigate";
    position_bucket: "1" | "2–3" | "4–10" | "11+";
    distance_km_bucket: string;
  };
  explore_empty_state: {
    mode: "places" | "farms" | "do";
    near_me: boolean;
    near_me_bucket: "yes" | "no";
    sort: string;
    has_search: boolean;
    has_search_bucket: "yes" | "no";
    has_filters: boolean;
    has_filters_bucket: "yes" | "no";
    has_geo: boolean;
    has_geo_bucket: "yes" | "no";
    radius_bucket: string;
  };
  explore_fallback_shown: {
    requested_mode: "places" | "farms" | "do";
    fallback_to: "farms";
    requested_mode_bucket: "places" | "farms" | "do";
  };

  /* ── Explore location ── */
  explore_location_request: { method: "geo" };
  explore_location_denied: { error: string };
  explore_location_set: { method: "geo" | "city"; city?: string };
  explore_location_clear: Record<string, never>;

  /* ── Lens ── */
  lens_opened: {
    lens_id: string;
    source: "direct" | "share" | "internal";
    has_results: boolean;
    results_bucket: "0" | "1-3" | "4-10" | "10+";
  };
  lens_item_clicked: {
    lens_id: string;
    item_type: "place" | "activity";
    item_id: string;
    position: number;
  };
  lens_empty_shown: {
    lens_id: string;
    reason: "no_results";
  };

  /* ── Activity template ── */
  activity_template_selected: {
    template_id: string;
    producer_id: string | undefined;
  };

  /* ── Activity publish/draft ── */
  activity_publish_clicked: {
    activity_id: string;
    producer_id: string;
    booking_mode: string;
    price_bucket: "free" | "paid";
    timing_mode: string;
  };
  activity_publish_succeeded: {
    activity_id: string;
    producer_id: string;
  };
  activity_publish_failed: {
    activity_id: string;
    producer_id: string;
    failure_reason: string;
    http_status?: number;
  };
  activity_draft_saved: {
    activity_id: string;
    producer_id: string;
  };
  activity_draft_save_failed: {
    activity_id: string;
    producer_id: string;
    failure_reason: string;
    http_status?: number;
  };

  /* ── Booking ── */
  booking_modal_opened: {
    activity_id: string;
    producer_id: string;
    booking_mode: string;
    price_bucket: "free" | "paid";
    spots_bucket: "unlimited" | "1-5" | "6-20" | "20+";
  };
  booking_submitted: {
    activity_id: string;
    producer_id: string;
    spots_requested_bucket: "1" | "2" | "3+";
    booking_mode: string;
  };
  booking_succeeded: {
    activity_id: string;
    producer_id: string;
  };
  booking_failed: {
    activity_id: string;
    producer_id: string;
    failure_reason: string;
    http_status?: number;
  };

  /* ── Media ── */
  media_upload_started: {
    owner_type: string;
    owner_id: string;
    file_size_bucket: "<1mb" | "1-5mb" | "5-20mb" | "20mb+";
    mime_group: "image" | "video" | "other";
  };
  media_upload_succeeded: {
    owner_type: string;
    owner_id: string;
    file_size_bucket: "<1mb" | "1-5mb" | "5-20mb" | "20mb+";
    mime_group: "image" | "video" | "other";
  };
  media_upload_failed: {
    owner_type: string;
    owner_id: string;
    file_size_bucket: "<1mb" | "1-5mb" | "5-20mb" | "20mb+";
    mime_group: "image" | "video" | "other";
    failure_reason: string;
  };
  media_finalize_failed: {
    owner_type: string;
    owner_id: string;
    failure_reason: string;
    http_status?: number;
  };

  /* ── Share (client) ──
     Spec uses "activity" (not "event") — we accept legacy "event" but normalize in track().
  */
  share_clicked: {
    share_kind: "place" | "producer" | "area" | "activity" | "event";
    share_id_or_slug: string;
    share_surface: string;
  };
  share_native_opened: {
    share_kind: "place" | "producer" | "area" | "activity" | "event";
    share_surface: string;
  };
  share_fallback_clipboard: {
    share_kind: "place" | "producer" | "area" | "activity" | "event";
    reason: "no_navigator_share" | "cannot_share" | "error";
  };
  share_failed: {
    share_kind: "place" | "producer" | "area" | "activity" | "event";
    failure_reason: string;
  };

  /* ── Favorites ── */
  favorite_added: {
    kind: "place" | "activity" | "producer";
    entity_id: string;
    location: "card" | "detail" | "favorites_list";
  };
  favorite_removed: {
    kind: "place" | "activity" | "producer";
    entity_id: string;
    location: "card" | "detail" | "favorites_list";
  };
  favorites_opened: {
    filter_kind: "all" | "place" | "activity" | "producer";
  };
  favorite_login_prompted: {
    kind: "place" | "activity" | "producer";
    entity_id: string;
    location: "card" | "detail" | "favorites_list";
    method: "sheet" | "toast_fallback";
  };
  favorite_login_clicked: {
    kind: "place" | "activity" | "producer";
    entity_id: string;
    location: "card" | "detail" | "favorites_list";
    method: "sheet" | "toast_fallback";
  };

  /* ── Experience ── */
  experience_opened: {
    experience_slug: string;
    surface: SpecSurface;
    href: string;
    position: number | null;
  };

  /* ── Place hero carousel ── */
  place_hero_image_changed: {
    place_id: string;
    place_slug: string | null;
    index: number;
    images_count: number;
  };

  /* ── Producer hero carousel ── */
  producer_hero_image_changed: {
    producer_id: string;
    producer_slug: string | null;
    index: number;
    images_count: number;
  };

  /* ── Event hero carousel ── */
  event_hero_image_changed: {
    event_id: string;
    index: number;
    images_count: number;
  };

  /* ── Core page views ── */
  place_viewed: {
    place_id: string;
    place_slug: string | null;
    href: string;
  };
  activity_opened: {
    activity_id: string;
    activity_slug: string | null;
    event_type: string | null;
    place_id: string | null;
    producer_id: string | null;
    href: string;
  };
  booking_started: {
    activity_id: string;
    producer_id: string | null;
    place_id: string | null;
    booking_mode: string | null;
    price_ore: number | null;
    max_spots: number | null;
  };
  booking_completed: {
    booking_id: string;
    activity_id: string;
    producer_id: string | null;
    place_id: string | null;
    spots: number | null;
    price_ore: number | null;
  };

  /* ── Host interest ── */
  host_interest_submitted: Record<string, never>;

  /* ── Onboarding ── */
  onboarding_intents_updated: {
    selected_count: number;
    selected_values: string[];
    surface: string;
    step: string;
  };
  publish_producer_failed: {
    error_code: string;
    is_validation: boolean;
  };
  producer_publish_succeeded: {
    surface: string;
  };

  /* ── Contact ── */
  contact_submitted: {
    page: string;
  };
  contact_submit_failed: {
    error_code: string;
    message: string;
  };

  /* ── Admin outbox ── */
  admin_outbox_viewed: Record<string, never>;
  admin_outbox_process_clicked: { status_filter: string };
  admin_outbox_retry_clicked: { outbox_id: string };
  admin_outbox_send_single_clicked: { outbox_id: string };

  /* ── Search ── */
  search_performed: {
    query: string;
    query_redacted: boolean;
    result_count: number;
    filters: Record<string, unknown> | null;
    geo_mode: string | null;
    debounced: false;
  };
  search_no_results: {
    query: string;
    query_redacted: boolean;
    filters: Record<string, unknown> | null;
    geo_mode: string | null;
  };
  search_result_clicked: {
    query: string;
    query_redacted: boolean;
    item_type: "place" | "activity" | "producer";
    item_id: string;
    rank: number;
    href: string;
  };

  /* ── Place Context Module ── */
  place_context_rendered: {
    place_id: string;
    events_count: number;
    places_count: number;
  };
  place_context_item_clicked: {
    source_place_id: string;
    target_type: "event" | "place";
    target_id: string;
  };

  /* ── My Page ── */
  mypage_opened: {
    surface: "my_page_overview" | "my_page_profile";
  };
  mypage_cta_clicked: {
    cta: "become_host" | "tip_us" | "tip_host" | "suggest_place";
  };
  mypage_saved_item_clicked: {
    kind: "place" | "activity" | "producer";
  };
  mypage_activity_clicked: {
    activity_id: string;
  };
 "saved_feed.impression": {
  items_count: number;
  event_ids: string[];
};
"saved_feed.click": {
  event_id: string;
  badge: "Snart" | "Nytt" | null;
};

  /* ── Profile identity ── */
  profile_identity_saved: {
    has_display_name: boolean;
    has_avatar: boolean;
  };
  profile_avatar_uploaded: {
    success: boolean;
  };
  profile_identity_cleared: {
    field: "display_name" | "avatar";
  };

  /* ── Products / Gårdsbutik ── */
  product_viewed: {
    product_id: string;
    producer_id: string;
    surface: string;
  };
  product_opened: {
    product_id: string;
    producer_id: string;
    surface: string;
  };
  shop_opened: {
    producer_id: string;
    surface: string;
  };
  shop_section_viewed: {
    producer_id: string;
    product_count: number;
  };
  inquiry_clicked: {
    product_id: string;
    producer_id: string;
    surface: string;
  };
  product_interest_clicked: {
    product_id: string;
    producer_id: string;
    surface: string;
  };
  product_order_started: {
    product_id: string;
    producer_id: string;
    price_bucket: "free" | "paid";
  };
  product_order_completed: {
    order_id: string;
    producer_id: string;
    items_count: number;
  };
  pickup_order_started: {
    producer_id: string;
    product_ids: string[];
    qty_total: number;
  };
  pickup_order_completed: {
    order_id: string;
    producer_id: string;
    total_amount: number;
  };
  shop_preorder_opened: {
    producer_id: string;
    product_id: string;
    source_surface: string;
  };
  shop_product_viewed: {
    product_id: string;
    producer_id: string;
    source_surface: string;
  };

  /* ── Cross-surface navigation ── */
  producer_link_clicked: {
    producer_id: string;
    event_id: string;
    source_surface: string;
  };
  producer_pickup_instructions_updated: {
    producer_id: string;
  };
  product_pickup_instructions_updated: {
    producer_id: string;
    product_id: string;
  };

  /* ── Share Kit (producer portal) ── */
  share_kit_opened: {
    activity_id: string;
    producer_id: string;
  };
  share_platform_selected: {
    activity_id: string;
    producer_id: string;
    platform: 'instagram' | 'facebook' | 'bio';
  };
  share_copy_clicked: {
    activity_id: string;
    producer_id: string;
    platform: 'instagram' | 'facebook' | 'bio';
    kind: 'text' | 'link';
  };

  /* ── Share Studio ── */
  share_studio_opened: {
    target_type: string;
    target_id: string;
    platform: string;
  };
  share_asset_generated: {
    format: string;
    template_key: string;
    success: boolean;
    duration_ms: number;
    target_type: string;
    pixel_ratio?: number;
    error_message?: string;
    retry_without_hero?: boolean;
    asset_type?: 'single' | 'carousel';
    slide_index?: number;
  };
  share_studio_link_copied: {
    target_type: string;
    target_id: string;
    platform: string;
  };
  share_studio_caption_copied: {
    target_type: string;
    target_id: string;
    platform: string;
    variant: string;
  };
  share_studio_asset_downloaded: {
    target_type: string;
    target_id: string;
    format: string;
    template_key: string;
  };
  share_studio_quick_action: {
    target_type: string;
    target_id: string;
    platform: string;
    format: string;
    template_key: string;
  };
  share_studio_open_app: {
    app: string;
    target_type: string;
    target_id: string;
  };
  editorial_handoff_requested: {
    source_type: string;
    target_type: string;
    surface: 'sheet' | 'lite';
    result: 'success' | 'error';
  };

  /* ── Share Prompts ── */
  share_prompt_clicked: {
    prompt_type: string;
    entity_type: string;
    entity_id: string;
    producer_id: string;
  };
  share_prompt_dismissed: {
    prompt_id: string;
  };

  /* ── Visitor Share ── */
  visitor_share_clicked: {
    activity_id: string;
    method: string;
  };
  visitor_share_completed: {
    activity_id: string;
    method: string;
  };

  /* ── Share Studio Lite ── */
  share_lite_share_clicked: {
    activity_id: string;
    has_native_share: boolean;
  };
  share_lite_native_opened: {
    activity_id: string;
  };
  share_lite_fallback_clipboard: {
    activity_id: string;
  };
  share_lite_failed: {
    activity_id: string;
  };
  share_lite_copy_caption: {
    activity_id: string;
  };
  publish_success_prompt_shown: {
    activity_id: string;
    producer_id: string;
  };

  host_dashboard_viewed: {
    producer_id: string;
    range_days: number;
  };

  /* ── Explore 12/10 ── */
  explore_opened: {
    surface: string;
    default_tab: string;
    has_location: boolean;
  };
  explore_search_in_area_clicked: Record<string, never>;
  explore_card_clicked: {
    type: "place" | "activity" | "producer";
    id: string;
    position_index: number;
    section_name: string;
  };
  explore_map_moved: {
    bounds_center_lat: number;
    bounds_center_lng: number;
  };
  explore_city_selected: {
    city: string;
  };

  /* ── Place Presentation (Producer Portal) ── */
  place_presentation_opened: {
    producer_id: string;
    place_id: string | null;
    is_public: boolean;
  };
  place_presentation_preview_clicked: {
    producer_id: string;
    place_id: string | null;
  };
  place_presentation_publish_clicked: {
    producer_id: string;
    place_id: string | null;
  };
  place_presentation_unpublish_clicked: {
    producer_id: string;
    place_id: string | null;
  };

  /* ── Place Editor Steps ── */
  place_editor_step_opened: {
    step: number;
    surface: string;
  };
  place_editor_step_completed: {
    step: number;
    surface: string;
  };

  /* ── Producer Dashboard ── */
  producer_dashboard_opened: {
    producer_id: string;
    is_public: boolean;
  };
  producer_primary_cta_clicked: {
    cta_type: 'create_first' | 'finish_draft' | 'create_new' | 'complement_activity';
    producer_id: string;
  };
  offerings_next_step_clicked: {
    producer_id: string;
    kind: 'shop_enable' | 'create_activity';
  };

  /* ── Editor Studio ── */
  editor_studio_opened: {
    place_id: string;
    role: string;
  };
  editor_studio_preview_clicked: Record<string, never>;
  editor_studio_save_clicked: {
    dirty_fields_count: number;
  };
  editor_studio_save_succeeded: Record<string, never>;
  editor_studio_save_failed: {
    error_key: string;
    reason_kind: string;
  };
  editor_studio_panel_card_clicked: {
    card: string;
  };

  /* ── Editor Seasons ── */
  editor_seasonal_mode_changed: {
    mode: string;
  };
  editor_season_selected: {
    season_key: string;
    enabled: boolean;
  };

  /* ── Admin Place Studio ── */
  admin_place_studio_opened: {
    place_id: string;
  };
  admin_place_publish_clicked: {
    place_id: string;
  };
  admin_place_publish_succeeded: {
    place_id: string;
  };
  admin_place_publish_failed: {
    place_id: string;
    error_key: string;
  };
  admin_place_unpublish_clicked: {
    place_id: string;
  };
  admin_place_unpublish_succeeded: {
    place_id: string;
  };
  admin_place_unpublish_failed: {
    place_id: string;
    error_key: string;
  };
  admin_place_quality_item_clicked: {
    item_key: string;
  };
  admin_place_ambassador_added: {
    place_id: string;
  };
  admin_place_ambassador_removed: {
    place_id: string;
  };

  /* ── Team (Värdteam) ── */
  team_viewed: { producer_id: string };
  team_invite_sent: { producer_id: string; action: string };
  team_invite_resent: { producer_id: string };
  team_role_changed: { producer_id: string; new_role: string };
  team_member_removed: { producer_id: string; role?: string; type?: string };
  team_owner_transfer_started: { producer_id: string };
  team_owner_transfer_completed: { producer_id: string };

  /* ── Producer qualification funnel ── */
  producer_qualify_started: { source_surface: string };
  producer_qualify_step_viewed: { step_index: string; step_id?: string };
  producer_qualify_completed: {
    has_place: string;
    producer_name_length: string;
    offerings_count: string;
    source_surface: string;
  };

  /* ── Producer interest submission ── */
  producer_interest_submit_started: Record<string, never>;
  producer_interest_submit_succeeded: Record<string, never>;
  producer_interest_submit_failed: { error_kind: string };

  /* ── Curated pages ── */
  curated_page_viewed: { page_slug: string };
  curated_item_clicked: {
    curated_page_slug: string;
    entity_type: string;
    entity_id: string;
    item_position: number;
  };
  curated_page_shared: { curated_page_slug: string };

  /* ── Editor writing mode ── */
  editor_writing_mode_hint_shown: { surface: string; field: string };
  editor_writing_mode_opened: { surface: string; field: string; source: 'button' | 'hint' };
  editor_writing_mode_closed: { surface: string; field: string };
}

type EventName = keyof AnalyticsEvents;

/* ─── State ──────────────────────────────────────── */

let _initialized = false;
let _viewerType: ViewerType = "anon";
let _env = "dev";

/* ─── Public API ─────────────────────────────────── */

export function initAnalytics() {
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
  const legacyKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const host =
    (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) ||
    "https://eu.i.posthog.com";
  const legacyHost = import.meta.env.VITE_POSTHOG_HOST as string | undefined;
  _env = (import.meta.env.VITE_APP_ENV as string | undefined) || "dev";

  if (!key) {
    if (_env === "dev") {
      console.warn(
        "[analytics] PostHog disabled: missing VITE_PUBLIC_POSTHOG_KEY. " +
          "Expected VITE_PUBLIC_POSTHOG_KEY / VITE_PUBLIC_POSTHOG_HOST. " +
          "Legacy VITE_POSTHOG_KEY / VITE_POSTHOG_HOST are ignored.",
        {
          appEnv: _env,
          legacyKeyPresent: Boolean(legacyKey),
          legacyHostPresent: Boolean(legacyHost),
        },
      );
    }
    return;
  }

  posthog.init(key, {
    api_host: host,
    autocapture: false,
    // PostHog Web Analytics requires native $pageview / $pageleave events.
    // These are the ONLY canonical pageview events. No custom "page_view" exists.
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: true,
    persistence: "localStorage",
  });

  _initialized = true;
}

export function identify(userId: string, traits?: Record<string, unknown>) {
  if (!_initialized) return;

  posthog.identify(userId);

  if (traits && Object.keys(traits).length) {
    posthog.people?.set?.(traits);
  }

  if (_viewerType === "anon") _viewerType = "auth";
}

let _surface: Surface = "desktop";
export function setSurface(surface: Surface) {
  _surface = surface;
}

export function setViewerType(type: ViewerType) {
  _viewerType = type;
}

/* ─── PII sanitizer for search queries ──────────── */

const RE_EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
const RE_PHONE = /\b(\+46|0)\s?\d{1,3}[\s-]?\d{2,3}[\s-]?\d{2,4}[\s-]?\d{2,4}\b/;
const RE_PERSONNUMMER = /\b\d{6}[-+]\d{4}\b|\b\d{8}[-+]\d{4}\b/;

export function sanitizeSearchQuery(raw: string): { query: string; query_redacted: boolean } {
  const trimmed = raw.trim().slice(0, 120);
  if (RE_EMAIL.test(trimmed) || RE_PHONE.test(trimmed) || RE_PERSONNUMMER.test(trimmed)) {
    return { query: "[redacted]", query_redacted: true };
  }
  return { query: trimmed, query_redacted: false };
}

/**
 * Normalize props that have legacy values which diverge from the spec.
 * We keep this minimal and only touch known offenders.
 */
function normalizeEventProps<E extends EventName>(event: E, props: AnalyticsEvents[E]) {
  // Normalize share_kind: "event" -> "activity"
  if (
    event === "share_clicked" ||
    event === "share_native_opened" ||
    event === "share_fallback_clipboard" ||
    event === "share_failed"
  ) {
    const p = props as any;
    if (p?.share_kind === "event") {
      p.share_kind = "activity";
    }
  }

  // Sanitize search query PII
  if (
    event === "search_performed" ||
    event === "search_no_results" ||
    event === "search_result_clicked"
  ) {
    const p = props as any;
    if (typeof p?.query === "string") {
      const { query, query_redacted } = sanitizeSearchQuery(p.query);
      p.query = query;
      p.query_redacted = query_redacted;
    }
  }

  return props;
}

export function track<E extends EventName>(
  event: E,
  props: AnalyticsEvents[E],
  source: Source = "router",
) {
  const normalizedProps = normalizeEventProps(event, { ...(props as any) });

  const enriched: BaseProps & AnalyticsEvents[E] = {
    ...(normalizedProps as any),
    env: _env,
    viewer_type: normalizeViewerType(_viewerType),
    source: normalizeSource(source),
    surface: normalizeSurface(_surface),
  };

  if (!_initialized) {
    if (_env === "dev") console.debug(`[analytics] ${event}`, enriched);
    return;
  }

  posthog.capture(event, enriched);
}

export function resetAnalytics() {
  if (!_initialized) return;
  posthog.reset();
  _viewerType = "anon";
}
